import { useCallback, useEffect, useState } from 'react';
import type {
  DistanceMatrixRow,
  NearbyPlace,
  Restaurant,
  RouteInfo,
  TimeWindow,
} from '@bilmat/shared';
import { apiGet } from '../lib/apiClient';
import { decodePolyline } from '../lib/polyline';
import { sampleSearchPoints, dedupeAndOrder } from '../lib/routeSampling';
import { toQueryValue } from '../lib/queryValue';
import { DETOUR_MAX } from '../lib/detour';
import { useApp, type LocationInput } from '../state/store';

const CANDIDATE_LIMIT = 20;
const RETRY_DELAY_MS = 600;

type State = {
  restaurants: Restaurant[];
  isLoading: boolean;
  error: string | null;
  retry: () => void;
};

/** Väntar `ms` men avbryts direkt om signalen aborteras. */
function delay(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    const t = window.setTimeout(resolve, ms);
    signal.addEventListener('abort', () => {
      window.clearTimeout(t);
      resolve();
    }, { once: true });
  });
}

function searchKey(
  origin: LocationInput,
  destination: LocationInput,
  timeWindow: TimeWindow,
): string | null {
  if (!origin.placeId || !destination.placeId) return null;
  return `${origin.placeId}::${destination.placeId}::${timeWindow.earliestMin}::${timeWindow.latestMin}`;
}

export function useRestaurantsAlongRoute(
  route: RouteInfo | null,
  origin: LocationInput,
  destination: LocationInput,
  timeWindow: TimeWindow,
): State {
  const restaurants = useApp((s) => s.restaurants);
  const setSearchResults = useApp((s) => s.setSearchResults);
  const [status, setStatus] = useState<{ isLoading: boolean; error: string | null }>({
    isLoading: false,
    error: null,
  });
  const [attempt, setAttempt] = useState(0);
  const retry = useCallback(() => setAttempt((a) => a + 1), []);

  const key = searchKey(origin, destination, timeWindow);

  useEffect(() => {
    if (!route || !origin.placeId || !destination.placeId || !key) {
      setSearchResults([], null);
      setStatus({ isLoading: false, error: null });
      return;
    }
    // Resultaten finns redan i storen för exakt samma sökning (t.ex. vid retur
    // från detaljskärmen) — visa dem direkt istället för en ny, dyr omsökning.
    if (useApp.getState().restaurantsKey === key) {
      setStatus({ isLoading: false, error: null });
      return;
    }
    const originValue = origin.placeId;
    const destValue = destination.placeId;
    const controller = new AbortController();
    setStatus({ isLoading: true, error: null });

    void (async () => {
      try {
        const path = decodePolyline(route.overviewPolyline);
        const { points, radiusMeters } = sampleSearchPoints({
          path,
          totalDurationSec: route.totalDurationSec,
          earliestMin: timeWindow.earliestMin,
          latestMin: timeWindow.latestMin,
        });

        const nearbyResults = await Promise.all(
          points.map((p) =>
            apiGet<{ places: NearbyPlace[] }>(
              'nearby',
              { location: `${p.lat},${p.lng}`, radius: String(radiusMeters) },
              controller.signal,
            )
              .then((d) => d.places)
              // Enstaka punkter får misslyckas (null), men avbrott och totalfel
              // ska upp till yttre catch — annars maskeras fel som "0 träffar"
              // och låses fast under sök-nyckeln.
              .catch((err: unknown) => {
                if (controller.signal.aborted) throw err;
                return null;
              }),
          ),
        );
        if (nearbyResults.length > 0 && nearbyResults.every((r) => r === null)) {
          throw new Error('nearby failed for all points');
        }

        const merged = nearbyResults.flatMap((r) => r ?? []);
        const candidates = dedupeAndOrder(merged, path, CANDIDATE_LIMIT);
        if (candidates.length === 0) {
          if (controller.signal.aborted) return;
          setSearchResults([], key);
          setStatus({ isLoading: false, error: null });
          return;
        }

        const candLatLng = candidates.map((c) => `${c.location.lat},${c.location.lng}`).join('|');

        const fetchMatrices = () =>
          Promise.all([
            apiGet<{ rows: DistanceMatrixRow[] }>(
              'distancematrix',
              { origins: toQueryValue(originValue), destinations: candLatLng },
              controller.signal,
            ),
            apiGet<{ rows: DistanceMatrixRow[] }>(
              'distancematrix',
              { origins: candLatLng, destinations: toQueryValue(destValue) },
              controller.signal,
            ),
          ]);

        // Ett automatiskt omförsök vid tillfälligt fel (Google-strypning/nätverksblipp)
        // innan vi ger upp och visar ett felmeddelande.
        let toCand: { rows: DistanceMatrixRow[] };
        let fromCand: { rows: DistanceMatrixRow[] };
        try {
          [toCand, fromCand] = await fetchMatrices();
        } catch {
          if (controller.signal.aborted) return;
          await delay(RETRY_DELAY_MS, controller.signal);
          if (controller.signal.aborted) return;
          [toCand, fromCand] = await fetchMatrices(); // sista försöket; kastar vidare till yttre catch
        }

        const reachRow = toCand.rows[0]?.elements ?? [];
        const restaurants: Restaurant[] = [];
        candidates.forEach((c, i) => {
          const reach = reachRow[i]?.durationSec ?? null;
          const toDest = fromCand.rows[i]?.elements[0]?.durationSec ?? null;
          if (reach == null || toDest == null) return;
          const detourMinutes = Math.round((reach + toDest - route.totalDurationSec) / 60);
          if (detourMinutes > DETOUR_MAX) return;
          const restaurant: Restaurant = {
            placeId: c.placeId,
            name: c.name,
            location: c.location,
            types: c.types,
            detourMinutes: Math.max(0, detourMinutes),
            timeToReachMinutes: Math.round(reach / 60),
          };
          if (c.rating != null) restaurant.rating = c.rating;
          if (c.userRatingsTotal != null) restaurant.userRatingsTotal = c.userRatingsTotal;
          if (c.priceLevel != null) restaurant.priceLevel = c.priceLevel;
          if (c.vicinity != null) restaurant.vicinity = c.vicinity;
          if (c.photoRef != null) restaurant.photoRef = c.photoRef;
          restaurants.push(restaurant);
        });

        setSearchResults(restaurants, key);
        setStatus({ isLoading: false, error: null });
      } catch {
        if (controller.signal.aborted) return;
        // Nollställ nyckeln så en ny sökning körs nästa gång (t.ex. vid retur),
        // istället för att låsa fast ett misslyckat resultat.
        setSearchResults([], null);
        setStatus({ isLoading: false, error: 'Kunde inte hämta restauranger.' });
      }
    })();

    return () => controller.abort();
  }, [
    route,
    origin.placeId,
    destination.placeId,
    timeWindow.earliestMin,
    timeWindow.latestMin,
    key,
    attempt,
    setSearchResults,
  ]);

  return { restaurants, isLoading: status.isLoading, error: status.error, retry };
}
